import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { adminGetNews, adminUpdateNews, adminDeleteNews, mediaUrl } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function AdminNews() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: items = [], isLoading } = useQuery({ queryKey: ["admin-news"], queryFn: adminGetNews });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-news"] });
    qc.invalidateQueries({ queryKey: ["news"] });
    qc.invalidateQueries({ queryKey: ["homepage-news"] });
  };

  const togglePublish = async (a) => {
    try {
      await adminUpdateNews(a.id, { status: a.status === "published" ? "draft" : "published" });
      invalidate();
      toast.success(a.status === "published" ? "Moved to draft" : "Published");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try { await adminDeleteNews(id); invalidate(); toast.success("Article deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "\u2014";

  return (
    <AdminShell title="News" actions={
      <button onClick={() => navigate("/admin/news/new")} data-testid="admin-add-news-button" className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-medium" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
        <Plus size={15} /> New Article
      </button>
    }>
      <p className="mb-4 text-sm" style={{ color: "var(--muted)" }}>Create and manage News articles and stories. Only published articles appear on the website and in the sitemap.</p>
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-[12px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }} />)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-[12px] border p-10 text-center" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
          <p className="text-sm" style={{ color: "var(--text)" }}>No articles yet.</p>
          <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>Create your first News article or story.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-news-list">
          {items.map((a) => {
            const cover = a.coverMediaId ? mediaUrl(a.coverMediaId) : (a.coverUrl || null);
            const published = a.status === "published";
            return (
              <div key={a.id} data-testid="admin-news-row" className="flex items-center gap-3 rounded-[12px] border px-3 py-3" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
                <div className="flex h-11 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border" style={{ borderColor: "var(--card-border)", background: "var(--bg-elev-1)" }}>
                  {cover ? <img src={cover} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px]" style={{ color: "var(--muted)" }}>No image</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium" style={{ color: "var(--text)" }}>{a.title}</p>
                    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px]" style={published ? { background: "rgba(140,106,61,0.16)", color: "var(--accent-fg)" } : { background: "rgba(255,255,255,0.05)", color: "var(--muted)" }}>{published ? "Published" : "Draft"}</span>
                  </div>
                  <p className="truncate text-xs" style={{ color: "var(--muted)" }}>{[a.category, fmt(a.publishedAt)].filter(Boolean).join("  \u00b7  ")}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {published && <a href={`/news/${a.slug}`} target="_blank" rel="noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--muted)" }} aria-label="View"><ExternalLink size={14} /></a>}
                  <button onClick={() => togglePublish(a)} data-testid="admin-news-toggle-publish" aria-label="Toggle publish" className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--muted)" }}>{published ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                  <button onClick={() => navigate(`/admin/news/edit/${a.id}`)} data-testid="admin-news-edit-button" aria-label="Edit" className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}><Pencil size={14} /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid="admin-news-delete-button" aria-label="Delete" className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--danger)" }}><Trash2 size={14} /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent style={{ background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" }}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this article?</AlertDialogTitle>
                        <AlertDialogDescription style={{ color: "var(--muted)" }}>"{a.title}" will be permanently removed and dropped from the sitemap. This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel style={{ background: "transparent", borderColor: "var(--card-border)", color: "var(--text)" }}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(a.id)} style={{ background: "var(--danger)", color: "#fff" }}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
